import type {
  InternalCheckoutConfig,
  InternalCheckoutFieldId,
} from '@maestro/commerce-blocks/theme/theme.types'
import type { CheckoutPaymentMode } from '@maestro/data-layer'

import { z } from 'zod'

import {
  emailField,
  phoneNumberField,
} from '@maestro/commerce-blocks/schemas/fields'
import { m } from '@maestro/commerce-blocks/schemas/helpers'

const deliveryDateField = z
  .string()
  .trim()
  .nonempty(m('delivery-date.required'))

/** ISO `yyyy-MM-dd` dates compare correctly as strings; empty is left to `nonempty`. */
const createMinDeliveryDateField = (minDeliveryDate: string) =>
  deliveryDateField.refine((value) => !value || value >= minDeliveryDate, {
    message: m('delivery-date.min', { date: minDeliveryDate }),
  })

/**
 * Base validator per field id (the "required" form). Optional fields wrap this
 * in `.optional()`; free-text `comment` has no non-empty constraint.
 */
const FIELD_BUILDERS: Record<InternalCheckoutFieldId, z.ZodType> = {
  customer: z.string().trim().nonempty(m('customer.required')).max(150),
  orderConfirmationEmail: emailField,
  reference: z.string().trim().nonempty(m('reference.required')).max(100),
  contactPerson: z
    .string()
    .trim()
    .nonempty(m('contact-person.required'))
    .max(150),
  contactPhone: phoneNumberField,
  comment: z.string().trim().max(500),
  deliveryDate: deliveryDateField,
}

/**
 * Builds the checkout form schema dynamically from the theme's `InternalCheckoutConfig`.
 * A field is present if it appears in `config.fields`; `isRequired: false`
 * wraps it in `.optional()`, which is the single control point the UI reads to
 * render the "optional" marker (see `useForm.registerField`, which derives
 * `required` from `!(field instanceof ZodOptional)`).
 * `minDeliveryDate` (ISO `yyyy-MM-dd`) rejects earlier delivery dates.
 */
export const createInternalCheckoutFormSchema = (
  config: InternalCheckoutConfig,
  { minDeliveryDate }: { minDeliveryDate?: string } = {}
) => {
  const shape: Record<string, z.ZodType> = {}

  for (const field of config.formFields.fields) {
    const base =
      field.id === 'deliveryDate' && minDeliveryDate
        ? createMinDeliveryDateField(minDeliveryDate)
        : FIELD_BUILDERS[field.id]
    shape[field.id] = field.isRequired
      ? base
      : z.union([base, z.string().trim().max(0)]).optional()
  }

  // Delivery-options checkbox group
  const deliveryOptions = config.deliveryOptions.options
  if (deliveryOptions.length > 0) {
    const optionShape = deliveryOptions.reduce(
      (acc, option) => {
        acc[option.id] = option.isRequired
          ? z.coerce
              .boolean()
              .refine((value) => value, { message: m(`${option.id}.required`) })
              .default(false)
          : z.coerce.boolean().default(false).optional()
        return acc
      },
      {} as Record<string, z.ZodType>
    )
    shape.deliveryOptions = z.object(optionShape)

    if (deliveryOptions.some((option) => option.id === 'subscription')) {
      shape.subscriptionInterval = z.string().optional()
    }
  }

  // Terms-of-sale acceptance (present if configured).
  if (config.summary.terms) {
    shape.termsAccepted = config.summary.terms.isCheckboxRequired
      ? z.coerce
          .boolean()
          .refine((value) => value, { message: m('terms.required') })
          .default(false)
      : z.coerce.boolean().default(false).optional()
  }

  // Payment-mode radios (present if configured).
  const paymentModes = Object.keys(config.summary.paymentModeOptions ?? {})
  if (paymentModes.length > 0) {
    shape.paymentMode = z.enum(paymentModes).optional()
  }

  return z.object(shape)
}

export type InternalCheckoutFormSchema = ReturnType<
  typeof createInternalCheckoutFormSchema
>

/**
 * Union of every field the template may produce. Because the actual shape is
 * built at runtime from `InternalCheckoutConfig`, all fields are optional here — the
 * schema enforces presence/requirement per store.
 */
export interface InternalCheckoutFormData {
  customer?: string
  orderConfirmationEmail?: string
  reference?: string
  contactPerson?: string
  contactPhone?: string
  comment?: string
  deliveryDate?: string
  deliveryOptions?: Record<string, boolean | undefined>
  subscriptionInterval?: string
  termsAccepted?: boolean
  paymentMode?: CheckoutPaymentMode
}
